import { computed, type Ref } from 'vue'
import { formatOgDateTime } from '../utils/ogMeta'

/**
 * Derives the Open Graph article values for a magazine article story
 * (ECOM-2071), shared by both brands' magazine pages.
 *
 * - og:image uses the article's OWN image (and teaser_image fallback), NOT the
 *   shared blogOverview section header — each article gets a distinct preview.
 * - published_time is the story's FIRST publish (`first_published_at`) and
 *   modified_time its latest publish (`published_at`, or the last save for
 *   unpublished drafts), matching what Open Graph means by each tag.
 * - Both are formatted with the Europe/Berlin offset and omitted when the story
 *   has no resolvable date (drafts carry an empty `published_at`).
 *
 * `story` is intentionally loosely typed: the per-brand generated
 * `StoryblokStory<SbBlogArticleV2>` types cannot live in the base layer, and a
 * structural `Ref` param would fight Ref invariance at the call sites.
 */
export default function useArticleOgMeta(story: Ref<any>) {
  const ogImage = computed<string | undefined>(
    () =>
      story.value?.content?.image?.[0]?.desktop_image?.filename ||
      story.value?.content?.teaser_image?.[0]?.desktop_image?.filename ||
      undefined,
  )

  const articlePublishedTime = computed(() =>
    formatOgDateTime(
      story.value?.first_published_at || story.value?.published_at,
    ),
  )

  const articleModifiedTime = computed(() =>
    formatOgDateTime(story.value?.published_at || story.value?.updated_at),
  )

  return { ogImage, articlePublishedTime, articleModifiedTime }
}
